package com.example.mystybag.appCore.views.pages

import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.expected.LocationsMapView
import com.example.mystybag.appCore.expected.WebHomeTopPickCardOverlay
import com.example.mystybag.appCore.logic.pages.HomePageViewModel
import com.example.mystybag.appCore.model.pages.HomeViewMode
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.AsyncImage
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.common.extensions.rippleClick
import com.example.mystybag.common.extensions.scrollEdgeFade
import com.example.mystybag.common.expected.hideWebHomeMapOverlayNow
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.components.native.Texts.StrikeThroughText
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p10
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p2
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.ui.theme.LocalTheme.p6
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.views.AppTheme
import dev.icerock.moko.resources.ImageResource
import com.example.mystybag.sharedmodels.project.responseModels.HomeTopPickUiModel
import kotlinx.coroutines.delay

@Composable
fun HomePageWebView(viewModel: HomePageViewModel) {
    val uiState by viewModel.uiState.subscribeAsState()
    val searchText by viewModel.searchControls.state.searchText.collectAsState()
    val listState = rememberLazyGridState()
    val mapListState = rememberLazyGridState()
    val selectedLocationsForSheet = uiState.selectedLocationsForSheet
    val colors = AppTheme.current.colors

    val maxFrameWidth = 1080.dp
    val listColumns = 4
    val mapColumns = 2
    val mapListHorizontalSpacing = 18.dp
    val mapSectionGap = 24.dp
    val listHorizontalSpacing = 20.dp
    val listVerticalSpacing = 20.dp
    val listGridWidth = maxFrameWidth - (listHorizontalSpacing * (listColumns - 1))
    val listCardWidth = (listGridWidth - (listHorizontalSpacing * (listColumns - 1))) / listColumns.toFloat()
    LaunchedEffect(Unit) {
        if (uiState.viewMode != HomeViewMode.MAP) {
            viewModel.onViewModeSelected(HomeViewMode.MAP)
        }
    }
    UIView(
        viewModel = viewModel,
        removeVerticalPadding = true,
        navigationContent = null,
        showTopBar = false,
        applyBottomInsetSpacer = false
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .background(colors.background)
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = 8.dp, bottom = 16.dp),
                horizontalArrangement = Arrangement.Center
            ) {
                Row(
                    modifier = Modifier
                        .width(maxFrameWidth)
                        .height(67.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    WebModeSwitch(
                        viewMode = uiState.viewMode,
                        onSelectMode = viewModel::onViewModeSelected
                    )
                    CategoryFilterRow(
                        selectedCategory = uiState.selectedCategory,
                        onCategorySelected = viewModel::onCategorySelected
                    )
                }
            }

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f),
                contentAlignment = Alignment.TopCenter
            ) {
                var displayedMode by remember { mutableStateOf(uiState.viewMode) }
                var isContentVisible by remember { mutableStateOf(true) }
                var isMapToGridCrossfade by remember { mutableStateOf(false) }
                var crossfadeTarget by remember { mutableStateOf(0f) }
                val contentAlpha by animateFloatAsState(
                    targetValue = if (isContentVisible) 1f else 0f,
                    animationSpec = tween(durationMillis = 180, easing = FastOutSlowInEasing),
                    label = "homeWebModeContentAlpha"
                )
                val mapToGridProgress by animateFloatAsState(
                    targetValue = crossfadeTarget,
                    animationSpec = tween(durationMillis = 220, easing = FastOutSlowInEasing),
                    label = "homeWebMapToGridProgress"
                )

                val listContent: @Composable () -> Unit = {
                    Row(
                        modifier = Modifier
                            .fillMaxHeight()
                            .fillMaxWidth(),
                        horizontalArrangement = Arrangement.Center
                    ) {
                        if (uiState.isLoading) {
                            LazyVerticalGrid(
                                state = listState,
                                columns = GridCells.Fixed(listColumns),
                                modifier = Modifier
                                    .width(listGridWidth)
                                    .fillMaxHeight()
                                    .scrollEdgeFade(
                                        showTop = false,
                                        edgeSize = 14.dp,
                                        overlayColor = colors.background.copy(alpha = 0.65f)
                                    ),
                                contentPadding = PaddingValues(bottom = 24.dp),
                                horizontalArrangement = Arrangement.spacedBy(listHorizontalSpacing),
                                verticalArrangement = Arrangement.spacedBy(listVerticalSpacing)
                            ) {
                                items(12) { HomeCardSkeleton(cardWidth = listCardWidth) }
                            }
                        } else if (uiState.gridItemsForGrid.isEmpty()) {
                            Box(
                                modifier = Modifier
                                    .width(listGridWidth)
                                    .fillMaxHeight(),
                                contentAlignment = Alignment.Center
                            ) {
                                Texts.BodyLarge(
                                    text = sharedString(SR.strings.no_bags_found),
                                    color = colors.onSurfaceVariant,
                                    textAlign = TextAlign.Center
                                )
                            }
                        } else {
                            LazyVerticalGrid(
                                state = listState,
                                columns = GridCells.Fixed(listColumns),
                                modifier = Modifier
                                    .width(listGridWidth)
                                    .fillMaxHeight()
                                    .scrollEdgeFade(
                                        showTop = false,
                                        edgeSize = 14.dp,
                                        overlayColor = colors.background.copy(alpha = 0.65f)
                                    ),
                                contentPadding = PaddingValues(bottom = 24.dp),
                                horizontalArrangement = Arrangement.spacedBy(listHorizontalSpacing),
                                verticalArrangement = Arrangement.spacedBy(listVerticalSpacing)
                            ) {
                                items(uiState.gridItemsForGrid, key = { it.id }) { item ->
                                    HomeTopPickCardWeb(
                                        model = item,
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .aspectRatio(186f / 248f),
                                        onClick = {
                                            hideWebHomeMapOverlayNow()
                                            viewModel.onTopPickClick(item)
                                        },
                                        onFavouriteClick = { viewModel.onFavouriteClick(item) }
                                    )
                                }
                            }
                        }
                    }
                }

                val mapContent: @Composable () -> Unit = {
                    BoxWithConstraints(
                        modifier = Modifier
                            .fillMaxWidth()
                            .fillMaxHeight(),
                        contentAlignment = Alignment.TopCenter
                    ) {
                        val frameWidth = maxWidth.coerceAtMost(maxFrameWidth)
                        val mapSectionWidth = (frameWidth - mapSectionGap).coerceAtLeast(0.dp) / 2f
                        Row(
                            modifier = Modifier
                                .width(frameWidth)
                                .fillMaxHeight(),
                            horizontalArrangement = Arrangement.spacedBy(mapSectionGap)
                        ) {
                            LazyVerticalGrid(
                                state = mapListState,
                                columns = GridCells.Fixed(mapColumns),
                                modifier = Modifier
                                    .width(mapSectionWidth)
                                    .fillMaxHeight()
                                    .scrollEdgeFade(
                                        showTop = false,
                                        edgeSize = 14.dp,
                                        overlayColor = colors.background.copy(alpha = 0.65f)
                                    ),
                                contentPadding = PaddingValues(0.dp),
                                horizontalArrangement = Arrangement.spacedBy(mapListHorizontalSpacing),
                                verticalArrangement = Arrangement.spacedBy(20.dp)
                            ) {
                                items(uiState.gridItemsForGrid, key = { it.id }) { item ->
                                    HomeTopPickCardWeb(
                                        model = item,
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .aspectRatio(255f / 340f),
                                        onClick = {
                                            hideWebHomeMapOverlayNow()
                                            viewModel.onTopPickClick(item)
                                        },
                                        onFavouriteClick = { viewModel.onFavouriteClick(item) }
                                    )
                                }
                            }
                            LocationsMapView(
                                modifier = Modifier
                                    .width(mapSectionWidth)
                                    .fillMaxHeight()
                                    .clip(RoundedCornerShape(32.dp)),
                                items = uiState.mapItems,
                                searchQuery = searchText,
                                userLatitude = uiState.userLatitude,
                                userLongitude = uiState.userLongitude,
                                onSearchQueryChange = viewModel.searchControls::onSearchTextChanged,
                                onLocationClick = viewModel::onLocationClick,
                                onLocationCardClick = viewModel::onLocationDialogTap,
                                onClusterClick = { locations -> viewModel.onLocationsAtSameAddress(locations) },
                                selectedItemId = selectedLocationsForSheet.firstOrNull()?.id
                            )
                        }
                    }
                }

                LaunchedEffect(uiState.viewMode) {
                    // Cold start / forced MAP (LaunchedEffect(Unit)): avoid 180ms where the switch shows MAP
                    // but the grid is still drawn while waiting for displayedMode to catch up.
                    if (uiState.viewMode == HomeViewMode.MAP && displayedMode != HomeViewMode.MAP && !isMapToGridCrossfade) {
                        displayedMode = HomeViewMode.MAP
                        isContentVisible = true
                        return@LaunchedEffect
                    }
                    if (uiState.viewMode == displayedMode) return@LaunchedEffect
                    if (displayedMode == HomeViewMode.MAP && uiState.viewMode == HomeViewMode.LIST) {
                        isMapToGridCrossfade = true
                        crossfadeTarget = 0f
                        isContentVisible = true
                        crossfadeTarget = 1f
                        delay(220)
                        displayedMode = HomeViewMode.LIST
                        isMapToGridCrossfade = false
                        crossfadeTarget = 0f
                        return@LaunchedEffect
                    }
                    isContentVisible = false
                    delay(180)
                    displayedMode = uiState.viewMode
                    isContentVisible = true
                }

                if (isMapToGridCrossfade) {
                    Box(modifier = Modifier.fillMaxSize().alpha(1f - mapToGridProgress)) {
                        mapContent()
                    }
                    Box(modifier = Modifier.fillMaxSize().alpha(mapToGridProgress)) {
                        listContent()
                    }
                } else {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .alpha(contentAlpha)
                    ) {
                        if (displayedMode == HomeViewMode.LIST) {
                            listContent()
                        } else {
                            mapContent()
                        }
                    }
                }
        }
    }
}
}

@Composable
private fun HomeTopPickCardWeb(
    model: HomeTopPickUiModel,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
    onFavouriteClick: () -> Unit
) {
    // On web the card is rendered as a DOM overlay (returns true) so URL images bypass CORS;
    // on every other platform the expect/actual returns false and we fall through to the
    // Compose-drawn card below.
    if (WebHomeTopPickCardOverlay(model = model, modifier = modifier, onClick = onClick, onFavouriteClick = onFavouriteClick)) {
        return
    }

    Box(
        modifier = modifier
            .clip(RoundedCornerShape(24.dp))
            .noRippleClickable { onClick() }
    ) {
        AsyncImage(
            modifier = Modifier.fillMaxSize(),
            link = model.locationCoverUrl,
            placeholder = null,
            useOriginalSize = false,
            contentScale = ContentScale.Crop
        )

        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(
                    brush = Brush.verticalGradient(
                        colors = listOf(
                            Color.Transparent,
                            Color.Black.copy(alpha = 0.15f),
                            Color.Black.copy(alpha = 0.5f),
                            Color.Black.copy(alpha = 0.88f)
                        )
                    )
                )
        )

        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(p16),
            verticalArrangement = Arrangement.SpaceBetween
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(
                    verticalArrangement = Arrangement.spacedBy(p6),
                    horizontalAlignment = Alignment.Start
                ) {
                    RatingBadge(
                        rating = model.rating,
                        reviewCount = model.reviewCount
                    )
                    if (model.isSellingFast) {
                        SellingFastBadgeWeb()
                    }
                }
                Box(
                    modifier = Modifier
                        .size(24.dp)
                        .clip(CircleShape)
                        .background(Color.White)
                        .noRippleClickable { onFavouriteClick() },
                    contentAlignment = Alignment.Center
                ) {
                    Image(
                        painter = dev.icerock.moko.resources.compose.painterResource(
                            if (model.isFavorite) SRC.images.nav_favorites else SRC.images.home_top_pick_heart
                        ),
                        modifier = Modifier.size(14.dp),
                        colorFilter = if (model.isFavorite) ColorFilter.tint(AppTheme.current.colors.onPrimaryContainer) else null
                    )
                }
            }

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = p10),
                verticalArrangement = Arrangement.spacedBy(p4)
            ) {
                Texts.BodyLarge(
                    text = model.title,
                    color = Color.White,
                    maxLines = 2
                )

                Column(verticalArrangement = Arrangement.spacedBy(p2)) {
                    Texts.BodySmall(
                        text = model.subtitle,
                        color = Color.White.copy(alpha = 0.8f),
                        maxLines = 1
                    )
                    Texts.BodySmall(
                        text = model.collectInfo,
                        color = Color.White.copy(alpha = 0.8f),
                        maxLines = 1
                    )
                }

                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(p8)
                ) {
                    StrikeThroughText(
                        text = model.oldPrice,
                        style = AppTheme.current.typography.bodySmall,
                        color = Color.White.copy(alpha = 0.8f),
                        maxLines = 1
                    )
                    Texts.TitleMedium(
                        text = model.newPrice,
                        color = AppTheme.current.colors.primary,
                        maxLines = 1
                    )
                }
            }
        }
    }
}

@Composable
private fun SellingFastBadgeWeb(modifier: Modifier = Modifier) {
    Box(
        modifier = modifier
            .height(20.dp)
            .clip(RoundedCornerShape(100.dp))
            .background(AppTheme.current.colors.primaryContainer)
            .padding(horizontal = p8),
        contentAlignment = Alignment.Center
    ) {
        Texts.LabelSmall(
            text = sharedString(SR.strings.selling_fast),
            color = AppTheme.current.colors.onPrimaryContainer,
            maxLines = 1
        )
    }
}

@Composable
private fun WebHeaderIconButton(
    icon: ImageResource,
    onClick: () -> Unit
) {
    val colors = AppTheme.current.colors
    Box(
        modifier = Modifier
            .size(48.dp)
            .clip(CircleShape)
            .background(colors.surfaceContainerLowest)
            .rippleClick(action = onClick),
        contentAlignment = Alignment.Center
    ) {
        Image(
            painter = dev.icerock.moko.resources.compose.painterResource(icon),
            modifier = Modifier.size(22.dp),
            colorFilter = ColorFilter.tint(colors.onSurfaceVariant)
        )
    }
}

@Composable
private fun WebModeSwitch(
    viewMode: HomeViewMode,
    onSelectMode: (HomeViewMode) -> Unit
) {
    val colors = AppTheme.current.colors
    // Figma: inactive track = light gray; active pill = white (surface / On Primary Variant);
    // active icon/label = primary green; inactive = onSurfaceVariant (same pattern as Welcome/Bags segments).
    val shape = RoundedCornerShape(126.dp)
    // Outer height > 40dp inner so Title/Label line metrics + icon are not clipped by clip(shape) on web.
    Box(
        modifier = Modifier
            .width(247.dp)
            .height(52.dp)
            .clip(shape)
            .background(colors.surfaceContainerLowest)
            .padding(horizontal = 4.dp, vertical = 4.dp)
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
            val segmentWidth = maxWidth / 2
            val targetFraction = if (viewMode == HomeViewMode.MAP) 0f else 1f
            val animatedFraction by animateFloatAsState(
                targetValue = targetFraction,
                animationSpec = tween(durationMillis = 260, easing = FastOutSlowInEasing),
                label = "webModeSwitchIndicator"
            )
            val slidingOffset = (segmentWidth.value * animatedFraction).dp

            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .offset(x = slidingOffset)
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth(0.5f)
                        .fillMaxHeight()
                        .clip(RoundedCornerShape(126.dp))
                        .background(colors.surface)
                )
            }

            Row(
                modifier = Modifier.fillMaxSize(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                WebModeSwitchItem(
                    selected = viewMode == HomeViewMode.MAP,
                    label = sharedString(SR.strings.map),
                    icon = SRC.images.home_view_map,
                    onClick = { onSelectMode(HomeViewMode.MAP) }
                )
                WebModeSwitchItem(
                    selected = viewMode == HomeViewMode.LIST,
                    label = sharedString(SR.strings.list),
                    icon = SRC.images.home_view_grid,
                    onClick = { onSelectMode(HomeViewMode.LIST) }
                )
            }
        }
    }
}

@Composable
private fun RowScope.WebModeSwitchItem(
    selected: Boolean,
    label: String,
    icon: ImageResource,
    onClick: () -> Unit
) {
    val colors = AppTheme.current.colors
    Box(
        modifier = Modifier
            .weight(1f)
            .fillMaxHeight()
            .rippleClick(action = onClick),
        contentAlignment = Alignment.Center
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Image(
                painter = dev.icerock.moko.resources.compose.painterResource(icon),
                modifier = Modifier.size(20.dp),
                colorFilter = ColorFilter.tint(
                    if (selected) colors.primary else colors.onSurfaceVariant
                )
            )
            // labelLarge (14/20) fits the segment row; titleMedium (16/24) was clipped on WASM/web.
            Texts.LabelLarge(
                text = label,
                color = if (selected) colors.primary else colors.onSurfaceVariant,
                fontWeight = FontWeight.Medium
            )
        }
    }
}
